import * as React from 'react';

import * as api from '@gitbook/api-client';
import { FREE_FONTS } from '@gitbook/app-database';
import {
    createPalette,
    PaletteButton,
    type PaletteCommandDef,
    PaletteCommandType,
} from '@gitbook/react-uikit';
import { Icon } from '@gitbook/spine-icons';
import { ButtonGroup, ButtonSkeleton, FieldGroup, Group, Label } from '@gitbook/spine-react';

import { type UseUpgradeSite, useUpgradeSite } from '../../../../state';
import { getPaidIndicatorBadgeProps, PaidFeatureIndicator } from '../../../Site';
import type { SiteOnlyCustomizationProps } from '../../types';
import { useCustomFontState } from './CustomFontState';
import { EditCustomFontButton } from './EditCustomFontButton';

interface AbstractFontSettingsProps<
    TFont extends api.CustomizationFont | api.CustomizationMonospaceFont,
> extends SiteOnlyCustomizationProps {
    /**
     * Font extracted from the customization settings.
     */
    font: TFont;
    /**
     * Function to set the font in the customization settings.
     */
    setFont: (font: TFont) => void;
    /**
     * Label to display for the font settings.
     */
    label: string;
    /**
     * List of default fonts to show in the palette.
     * This should be a list of all the default fonts that can be selected.
     */
    defaultFonts: Array<NonNullable<TFont>>;
}

/**
 * Settings to configure the font of the content.
 */
export function AbstractFontSettings<
    TFont extends api.CustomizationFont | api.CustomizationMonospaceFont,
>(props: AbstractFontSettingsProps<TFont>) {
    const { site, font, label } = props;
    const upgradePremiumFonts = useUpgradeSite(site, api.SiteFeatureKey.SitesAdvancedCustomization);
    const upgradeCustomFonts = useUpgradeSite(site, api.SiteFeatureKey.SitesCustomFonts);

    const { upgrade, isFeatureActive } = (() => {
        if (!font) {
            return { upgrade: null, isFeatureActive: false };
        }

        if (typeof font === 'string') {
            return {
                upgrade: upgradePremiumFonts,
                isFeatureActive: !checkIsFreeFont(font),
            };
        }

        if (font.custom) {
            return {
                upgrade: upgradeCustomFonts,
                isFeatureActive: true,
            };
        }

        return { upgrade: null, isFeatureActive: false };
    })();

    return (
        <FieldGroup orientation="horizontal">
            <Group>
                <Label>{label}</Label>
                <PaidFeatureIndicator upgrade={upgrade} isFeatureActive={isFeatureActive} />
            </Group>
            <React.Suspense fallback={<ButtonSkeleton label="Font" />}>
                <FontPalette
                    {...props}
                    upgradePremiumFonts={upgradePremiumFonts}
                    upgradeCustomFonts={upgradeCustomFonts}
                />
            </React.Suspense>
        </FieldGroup>
    );
}

/**
 * Palette to pick a default or custom font
 */
export function FontPalette<TFont extends api.CustomizationFont | api.CustomizationMonospaceFont>(
    props: AbstractFontSettingsProps<TFont> & {
        upgradePremiumFonts: UseUpgradeSite | null;
        upgradeCustomFonts: UseUpgradeSite | null;
    }
) {
    const { font, setFont, upgradePremiumFonts, upgradeCustomFonts, defaultFonts } = props;

    const customFontState = useCustomFontState({
        organizationId: props.organizationId,
        upgrade: upgradeCustomFonts,
        value: font && typeof font === 'object' ? font : null,
        onChange: (font) => {
            setFont(font as TFont);
        },
    });

    const fontValue = font ? getValue(font) : null;

    return (
        <>
            <ButtonGroup kind="separated" border={false} disabled={Boolean(props.siteSpaceId)}>
                <PaletteButton
                    kind="secondary"
                    trailingIcon={Icon.ChevronDown}
                    label={getDisplayName(font)}
                    title={
                        props.siteSpaceId
                            ? 'You can edit font settings at the site level'
                            : undefined
                    }
                    palette={() =>
                        createPalette({
                            groups: [
                                ...(customFontState?.paletteGroups ?? []),
                                {
                                    key: 'default-fonts',
                                    title: 'Default Fonts',
                                    commands: defaultFonts.map((iFont) => {
                                        const value = getValue(iFont);
                                        const isFree = checkIsFreeFont(iFont);
                                        const isActive = fontValue === value;
                                        const baseProps = {
                                            key: value,
                                            title: getDisplayName(iFont),
                                            upgrade: !isFree,
                                            badge: getPaidIndicatorBadgeProps({
                                                upgrade: isFree ? null : upgradePremiumFonts,
                                                isFeatureActive: isActive,
                                            }),
                                            trigger: () => {
                                                setFont(iFont);
                                            },
                                        };
                                        if (!isActive) {
                                            return baseProps satisfies PaletteCommandDef;
                                        }
                                        return {
                                            ...baseProps,
                                            type: PaletteCommandType.RADIO,
                                            selected: true,
                                        } satisfies PaletteCommandDef;
                                    }),
                                },
                            ],
                        })
                    }
                />
                {customFontState ? (
                    <EditCustomFontButton {...props} state={customFontState} />
                ) : null}
            </ButtonGroup>
        </>
    );
}

const displayNames: {
    [key in api.CustomizationDefaultFont | api.CustomizationDefaultMonospaceFont]: string;
} = {
    [api.CustomizationDefaultFont.Inter]: 'Inter',
    [api.CustomizationDefaultFont.Roboto]: 'Roboto',
    [api.CustomizationDefaultFont.ABCFavorit]: 'Favorit',
    [api.CustomizationDefaultFont.RobotoSlab]: 'Roboto Slab',
    [api.CustomizationDefaultFont.OpenSans]: 'Open Sans',
    [api.CustomizationDefaultFont.SourceSansPro]: 'Source Sans Pro',
    [api.CustomizationDefaultFont.Lato]: 'Lato',
    [api.CustomizationDefaultFont.Ubuntu]: 'Ubuntu',
    [api.CustomizationDefaultFont.Raleway]: 'Raleway',
    [api.CustomizationDefaultFont.Merriweather]: 'Merriweather',
    [api.CustomizationDefaultFont.Overpass]: 'Overpass',
    [api.CustomizationDefaultFont.NotoSans]: 'Noto Sans',
    [api.CustomizationDefaultFont.IBMPlexSerif]: 'IBM Plex Serif',
    [api.CustomizationDefaultFont.Poppins]: 'Poppins',
    [api.CustomizationDefaultFont.FiraSans]: 'Fira Sans',
    [api.CustomizationDefaultMonospaceFont.FiraCode]: 'Fira Code',
    [api.CustomizationDefaultMonospaceFont.IBMPlexMono]: 'IBM Plex Mono',
    [api.CustomizationDefaultMonospaceFont.JetBrainsMono]: 'JetBrains Mono',
    [api.CustomizationDefaultMonospaceFont.SourceCodePro]: 'Source Code Pro',
    [api.CustomizationDefaultMonospaceFont.RobotoMono]: 'Roboto Mono',
    [api.CustomizationDefaultMonospaceFont.SpaceMono]: 'Space Mono',
    [api.CustomizationDefaultMonospaceFont.DMMono]: 'DM Mono',
    [api.CustomizationDefaultMonospaceFont.Inconsolata]: 'Inconsolata',
};

function getDisplayName(
    font: api.CustomizationFont | api.CustomizationMonospaceFont | null
): string {
    if (!font) {
        return 'System';
    }

    if (typeof font === 'string') {
        return displayNames[font];
    }

    return font.fontFamily;
}

function getValue(font: api.CustomizationFont | api.CustomizationMonospaceFont): string {
    if (typeof font === 'string') {
        return font;
    }
    return font.id;
}

function checkIsFreeFont(font: api.CustomizationFont | api.CustomizationMonospaceFont): boolean {
    return FREE_FONTS.includes(font as api.CustomizationDefaultFont);
}
